import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { LifeBuoy, Upload, Loader2, Save, ExternalLink } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { formatDateTime } from "../../lib/format";
import { toast } from "sonner";

const CHUNK = 5 * 1024 * 1024;
const MAX = 200 * 1024 * 1024;
const mb = (n) => `${(n / 1024 / 1024).toFixed(1)} MB`;

export default function RemoteSupport() {
  const [cfg, setCfg] = useState(null);
  const [istr, setIstr] = useState("");
  const [saving, setSaving] = useState(false);
  const [webLink, setWebLink] = useState("");
  const [savingLink, setSavingLink] = useState(false);
  const saveWebLink = async () => {
    setSavingLink(true);
    try { const { data } = await api.put("/admin/remote-support/web-link", { web_link: webLink }); setCfg(data); setWebLink(data.web_link || ""); toast.success("Link RustDesk salvato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingLink(false); }
  };
  const [progress, setProgress] = useState(null);
  const fileInput = useRef(null);

  const load = () => api.get("/admin/remote-support").then(({ data }) => { setCfg(data); setIstr(data.istruzioni || ""); setWebLink(data.web_link || ""); })
    .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  const saveIstr = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/remote-support/istruzioni", { istruzioni: istr }); setCfg(data); toast.success("Istruzioni salvate"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const upload = async (f) => {
    if (f.size > MAX) { toast.error("Il file supera i 200 MB"); return; }
    setProgress(0);
    try {
      const { data: init } = await api.post("/admin/remote-support/upload/init", { name: f.name, size: f.size });
      const n = Math.max(1, Math.ceil(f.size / CHUNK));
      for (let i = 0; i < n; i++) {
        const fd = new FormData();
        fd.append("file", f.slice(i * CHUNK, (i + 1) * CHUNK), f.name);
        await api.post(`/admin/remote-support/upload/${init.upload_id}/chunk?index=${i}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setProgress(Math.round(((i + 1) / n) * 95));
      }
      const { data } = await api.post(`/admin/remote-support/upload/${init.upload_id}/complete`);
      setProgress(100); setCfg(data); toast.success("File caricato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTimeout(() => setProgress(null), 600); }
  };
  const onPick = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); };

  if (!cfg) return <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  const busy = progress !== null;

  return (
    <div data-testid="admin-remote-support">
      <PageHeader title="Assistenza Remota" subtitle="File eseguibile scaricato dagli studi da Impostazioni Studio → Assistenza Remota. Viene sempre usato l'ultimo file caricato." icon={LifeBuoy} />
      <div className="max-w-2xl rounded-xl border border-slate-800 bg-[#121824] p-6 space-y-6">
        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-300">File eseguibile (max 200 MB)</label>
          <div className="text-sm text-slate-200" data-testid="remote-support-file-info">
            {cfg.file ? <>{cfg.file.name} · {mb(cfg.file.size)} · caricato il {formatDateTime(cfg.file.uploaded_at)}</> : "Nessun file caricato"}
          </div>
          <Button disabled={busy} onClick={() => fileInput.current?.click()} data-testid="remote-support-upload-btn" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />} {cfg.file ? "Sostituisci file" : "Carica file"}
          </Button>
          <input ref={fileInput} type="file" className="hidden" onChange={onPick} data-testid="remote-support-file-input" />
          {busy && <div className="space-y-1" data-testid="remote-support-progress"><Progress value={progress} /><div className="text-xs text-slate-400">{progress}%</div></div>}
        </div>
        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-300">Istruzioni mostrate all'utente prima del download</label>
          <Textarea value={istr} onChange={(e) => setIstr(e.target.value)} rows={6} maxLength={5000}
            placeholder="Es. Avvia il file scaricato, consenti l'esecuzione e comunica all'operatore l'ID e la password che compaiono a schermo."
            className="bg-slate-900/60 border-slate-700" data-testid="remote-support-istruzioni-input" />
          <Button onClick={saveIstr} disabled={saving} data-testid="remote-support-istruzioni-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Salva istruzioni
          </Button>
        </div>
        <div className="space-y-2 border-t border-slate-800 pt-6">
          <label className="text-xs font-medium text-slate-300">Link sessione RustDesk web (visibile solo al superadmin)</label>
          <Input value={webLink} onChange={(e) => setWebLink(e.target.value)} placeholder="https://…" className="bg-slate-900/60 border-slate-700" data-testid="remote-support-weblink-input" />
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={saveWebLink} disabled={savingLink} data-testid="remote-support-weblink-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {savingLink ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Salva link
            </Button>
            {cfg.web_link && (
              <a href={cfg.web_link} target="_blank" rel="noopener noreferrer" data-testid="remote-support-weblink-open"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 underline break-all">
                <ExternalLink className="h-4 w-4 shrink-0" /> {cfg.web_link}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
